import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { DgNumberPipe } from '../../dg-number.pipe';
import { BomCellComponent } from '../bom-cell/bom-cell.component';

@Component({
  selector: 'app-suggested-products-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DgNumberPipe,
    BomCellComponent,
    TranslatePipe,
  ],
  template: `
    <div class="flex h-full flex-col">
    <div class="min-h-0 flex-1 overflow-auto">
      @if (loading) {
        <div class="flex flex-col gap-3">
          @for (row of skeletonRows; track row) {
            <div class="flex animate-pulse items-start justify-between rounded-xl border bg-card p-3">
              <div class="flex w-full flex-col gap-2">
                <div class="h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
              <div class="ml-3 flex shrink-0 flex-col items-end gap-3">
                <div class="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          }
        </div>
      } @else if (products.length) {
        <div class="flex flex-col gap-3">
          @for (product of products; track product) {
            <div class="flex items-start justify-between rounded-xl border bg-card p-3">
              <div class="flex min-w-0 flex-col gap-0.5">
                <span class="inline-flex items-center gap-1.5 font-semibold">{{ product.sku }}<app-bom-cell [material]="product.material" /></span>
                <span class="text-secondary text-sm uppercase">{{ product.name }}</span>
                <div class="text-primary flex flex-wrap gap-x-3 text-sm font-medium">
                  <!--
                    The empty-string fallbacks matter. These fields come straight off the
                    suggestions endpoint with no defaulting, and messageformat stringifies a
                    missing argument, so an absent value renders the word "undefined" where plain
                    Angular interpolation used to render nothing.
                  -->
                  <span>{{ 'DOCUMENT_GENERATE.COLUMNS.BASE_UOM_LABEL' | t: { value: product.umb ?? '' } }}</span>
                  <span>{{ 'DOCUMENT_GENERATE.COLUMNS.SECONDARY_UOM_LABEL' | t: { value: product.ums ?? '' } }}</span>
                  @if (umpActive && product.ump && product.ump !== '-') {
                  <span>{{ 'DOCUMENT_GENERATE.COLUMNS.PACKAGING_UOM_LABEL' | t: { value: product.ump ?? '' } }}</span>
                  }
                </div>
                @if (product.avgUnitName) {
                <span class="text-secondary text-xs">
                  {{ 'DOCUMENT_GENERATE.SUGGESTED_PRODUCTS_TAB.AVERAGE_PURCHASE' | t }} {{ product.avgQty | dgNumber }} {{ product.avgUnitName }}
                </span>
                }
              </div>
              <div class="flex flex-col items-end justify-between gap-3">
                <button mat-icon-button color="primary" type="button"
                  [matTooltip]="'DOCUMENT_GENERATE.SUGGESTED_PRODUCTS.ADD_TOOLTIP' | t" (click)="add.emit(product)">
                  <mat-icon>add_circle_outline</mat-icon>
                </button>
                <span class="text-secondary whitespace-nowrap text-sm">L.&nbsp;{{ product.price | dgNumber }}</span>
              </div>
            </div>
          }
        </div>
      } @else if (!loading) {
        <div class="text-secondary flex h-full items-center justify-center text-center text-sm">{{ emptyMessage || ('DOCUMENT_GENERATE.MESSAGES.NO_SUGGESTED_PRODUCTS' | t) }}</div>
      }
    </div>
    </div>
  `,
})
export class SuggestedProductsTabComponent {
  @Input() products: any[] = [];
  @Input() loading = false;
  /**
   * Already-translated copy. The parent owns it because the wording depends on the form state;
   * left empty, the template falls back to the generic "no suggested products" message.
   */
  @Input() emptyMessage = '';
  @Input() umpActive = true;
  @Output() add = new EventEmitter<any>();
  readonly skeletonRows = [1, 2, 3, 4];
}
